// src/components/modals/UserProfileModal.js

import React, { useContext, useState, useEffect } from 'react';
import { UserProfileContext } from '../../contexts/UserProfileContext';
import { createFieldEngineer } from '../../services/api/fieldEngineers';
import UserProfileIntroQuestion from './UserProfileIntroQuestion';
import UserProfileEditActions from './UserProfileEditActions';
import FormInput from '../../../shared/components/FormInput';

export default function UserProfileModal() {
  const {
    userProfile,
    showProfileModal,
    setShowProfileModal,
    updateUserProfile,
    getCurrentAccessKey
  } = useContext(UserProfileContext);

  const [tempGivenName, setTempGivenName] = useState(userProfile.given_name || '');
  const [tempFamilyName, setTempFamilyName] = useState(userProfile.family_name || '');
  const [tempEmail, setTempEmail] = useState(userProfile.email);
  const [tempCompany, setTempCompany] = useState(userProfile.company);
  const [tempPhone, setTempPhone] = useState(userProfile.phone);
  const [isCreatingFieldEngineer, setIsCreatingFieldEngineer] = useState(false);
  const [fieldEngineerError, setFieldEngineerError] = useState(null);

  const isFirstTime = !userProfile.given_name && !userProfile.family_name && !userProfile.email && !userProfile.company && !userProfile.phone;

  // Re-sync input fields whenever the modal is opened
  useEffect(() => {
    if (showProfileModal) {
      setTempGivenName(userProfile.given_name || '');
      setTempFamilyName(userProfile.family_name || '');
      setTempEmail(userProfile.email || '');
      setTempCompany(userProfile.company || '');
      setTempPhone(userProfile.phone || '');
    }
  }, [showProfileModal, userProfile]);

  async function handleSave() {
    if (!tempGivenName || !tempFamilyName || !tempEmail || !tempCompany || !tempPhone) {
      alert('Please fill out all fields.');
      return;
    }

    // Update profile first
    updateUserProfile({
      given_name: tempGivenName,
      family_name: tempFamilyName,
      email: tempEmail,
      company: tempCompany,
      phone: tempPhone
    });

    // Create field engineer if we have an access key and don't already have a field_engineer_id
    const accessKey = getCurrentAccessKey();
    if (accessKey && !userProfile.field_engineer_id) {
      setIsCreatingFieldEngineer(true);
      setFieldEngineerError(null);
      
      try {
        const data = await createFieldEngineer({
          access_key: accessKey,
          given_name: tempGivenName,
          family_name: tempFamilyName,
          company_name: tempCompany,
          phone_number: tempPhone,
          email: tempEmail
        });
        updateUserProfile({ field_engineer_id: data.id });
      } catch (err) {
        console.error('Error creating field engineer:', err);
        setFieldEngineerError('Failed to create field engineer. Please try again.');
        setIsCreatingFieldEngineer(false);
        return; // Don't close modal if field engineer creation failed
      } finally {
        setIsCreatingFieldEngineer(false);
      }
    }

    setShowProfileModal(false);
  }

  function handleCancel() {
    setShowProfileModal(false);
  }

  if (!showProfileModal) return null;

  return (
    <div className="profile-modal-overlay">
      <div className="card p-4" style={{ maxWidth: '400px', width: '100%' }}>
        <div className="card-body">
          <div className="text-center mb-3">
            <div
              className="rounded-circle d-inline-flex justify-content-center align-items-center"
              style={{
                width: '60px',
                height: '60px',
                backgroundColor: '#eef2ff'
              }}
            >
              <i className="bi bi-exclamation text-primary" style={{ fontSize: '1.5rem' }}></i>
            </div>
          </div>

          <h3 className="text-center mb-2">Welcome!</h3>
          <p className="text-center text-muted mb-4">
            Please tell us a bit about yourself
          </p>

          <FormInput
            id="profile-given-name"
            name="given_name"
            label="Given Name"
            type="text"
            placeholder="Enter your given name"
            value={tempGivenName}
            onChange={(e) => setTempGivenName(e.target.value)}
            icon="bi-person"
            required
            autoComplete="given-name"
            disabled={isCreatingFieldEngineer}
          />
          <FormInput
            id="profile-family-name"
            name="family_name"
            label="Family Name"
            type="text"
            placeholder="Enter your family name"
            value={tempFamilyName}
            onChange={(e) => setTempFamilyName(e.target.value)}
            icon="bi-person"
            required
            autoComplete="family-name"
            disabled={isCreatingFieldEngineer}
          />
          <FormInput
            id="profile-company"
            name="company"
            label="Company Name"
            type="text"
            placeholder="Enter company name"
            value={tempCompany}
            onChange={(e) => setTempCompany(e.target.value)}
            icon="bi-building"
            required
            autoComplete="organization"
            disabled={isCreatingFieldEngineer}
          />
          <FormInput
            id="profile-email"
            name="email"
            label="Email Address"
            type="email"
            placeholder="Enter email address"
            value={tempEmail}
            onChange={(e) => setTempEmail(e.target.value)}
            icon="bi-envelope"
            required
            autoComplete="email"
            disabled={isCreatingFieldEngineer}
          />
          <FormInput
            id="profile-phone"
            name="phone"
            label="Phone Number"
            type="tel"
            placeholder="Enter phone number"
            value={tempPhone}
            onChange={(e) => setTempPhone(e.target.value)}
            icon="bi-telephone"
            required
            autoComplete="tel"
            className="mb-4"
            disabled={isCreatingFieldEngineer}
          />

          {fieldEngineerError && (
            <div className="alert alert-danger mb-3">
              {fieldEngineerError}
            </div>
          )}

          {isFirstTime ? (
            <UserProfileIntroQuestion 
              onSave={handleSave} 
              onCancel={handleSave} 
              isLoading={isCreatingFieldEngineer}
            />
          ) : (
            <UserProfileEditActions 
              onSave={handleSave} 
              onCancel={handleCancel} 
              isLoading={isCreatingFieldEngineer}
            />
          )}
        </div>
      </div>
    </div>
  );
}
